import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Save } from 'lucide-react';
import { MedicalFormRenderer } from '../components/MedicalFormRenderer';
import { API_BASE } from '../config/env';
import { PublicMedicalFormView } from '../types/medicalForms';

export default function PublicMedicalFormPage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState<PublicMedicalFormView | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let active = true;
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/medical-forms/public/${token}`)
      .then(async (response) => {
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.message || `Request failed (${response.status})`);
        }
        return response.json();
      })
      .then((payload: PublicMedicalFormView) => {
        if (!active) return;
        setData(payload);
        setAnswers(payload.answers || {});
      })
      .catch((err: any) => {
        if (!active) return;
        setError(err.message || 'Unable to load form.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [token]);

  async function saveProgress() {
    if (!token) return;
    setSaving(true);
    setError(null);
    setBanner(null);
    try {
      const response = await fetch(`${API_BASE}/medical-forms/public/${token}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.message || `Request failed (${response.status})`);
      setData(payload);
      setBanner('Progress saved.');
    } catch (err: any) {
      setError(err.message || 'Unable to save progress.');
    } finally {
      setSaving(false);
    }
  }

  async function submitForm() {
    if (!token) return;
    if (!window.confirm('Submit this form now?')) return;
    setSubmitting(true);
    setError(null);
    setBanner(null);
    try {
      const response = await fetch(`${API_BASE}/medical-forms/public/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.message || `Request failed (${response.status})`);
      setData(payload);
      setBanner('Form submitted successfully. Thank you.');
    } catch (err: any) {
      setError(err.message || 'Unable to submit form.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500">Elen1 EMR</p>
          <h1 className="text-2xl font-semibold text-slate-900">Patient Medical Form</h1>
        </div>

        {banner && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">{banner}</div>}
        {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{error}</div>}

        {loading ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-sm text-slate-500 flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Loading form...
          </div>
        ) : !data ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-sm text-slate-500">This form is unavailable.</div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-lg font-semibold text-slate-900">{data.template.name}</p>
                <p className="text-xs text-slate-500">
                  {data.patient.displayName} • Completion {data.completionPercent}% • Status {data.status}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={saveProgress}
                  disabled={saving || submitting || data.status === 'COMPLETED'}
                  className="px-3 py-2 rounded border border-slate-300 bg-white text-sm disabled:opacity-60"
                >
                  <Save size={14} className="inline mr-1" />
                  {saving ? 'Saving...' : 'Save Draft'}
                </button>
                <button
                  onClick={submitForm}
                  disabled={submitting || data.status === 'COMPLETED'}
                  className="px-3 py-2 rounded bg-emerald-600 text-white text-sm disabled:opacity-60"
                >
                  {submitting ? 'Submitting...' : data.status === 'COMPLETED' ? 'Submitted' : 'Submit Form'}
                </button>
              </div>
            </div>

            <MedicalFormRenderer
              definition={data.template.definition}
              answers={answers}
              readOnly={data.status === 'COMPLETED'}
              onChange={(fieldId, value) => setAnswers((prev) => ({ ...prev, [fieldId]: value }))}
            />
          </div>
        )}
      </div>
    </div>
  );
}
